import { Badge, Button, ConfigProvider, Descriptions, Drawer, Space, Table, Timeline } from "antd";
import { EmptyWrapper } from "components/empty/EmptyComponent";
import {
  ICCloseOutlined,
  ICLeftOutlined,
  ICLoadingOutlined,
  ICRightOutlined,
} from "components/list/ListIcon";
import TableTitle from "components/tableSendiri/TableTitle";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { capitalizeFirstLetter, formatNumber, formatWIB, formatWIBString } from "utils/helpers";

// Map a v2 delivery status to an AntD Badge status + human label.
const statusBadge = (status) => {
  switch (status) {
    case "sent":
      return { status: "success", text: "Sent" };
    case "bounced":
      return { status: "error", text: "Bounced" };
    case "expired":
      return { status: "error", text: "Expired" };
    case "deferred":
      return { status: "warning", text: "Deferred" };
    case "in_queue":
      return { status: "processing", text: "In Queue" };
    default:
      return { status: "default", text: capitalizeFirstLetter(status || "-") };
  }
};

// Color the attempt timeline by DSN class: 2.x.x ok, 4.x.x temporary, 5.x.x permanent.
const dsnColor = (dsn) => {
  if (!dsn) return "gray";
  if (dsn.startsWith("2")) return "green";
  if (dsn.startsWith("4")) return "orange";
  if (dsn.startsWith("5")) return "red";
  return "gray";
};

const headerCSV = [
  { label: "Date (WIB)", key: "dateWIB" },
  { label: "Message ID", key: "messageId" },
  { label: "From", key: "from" },
  { label: "Recipient", key: "recipient" },
  { label: "Subject", key: "subject" },
  { label: "Status", key: "status" },
  { label: "DSN", key: "dsn" },
  { label: "Attempts", key: "attemptCount" },
  { label: "SMTP Out Host", key: "smtpOutHost" },
  { label: "Relay Host", key: "relayHost" },
  { label: "Remote Response", key: "remoteResponse" },
];

const TableDeliveries = ({
  filter,
  setFilter,
  hitApi,
  loading,
  emptyState,
  pageIndex = 1,
  canPrev = false,
  onPrev,
  onNext,
  onRefresh,
}) => {
  const [visible, setVisible] = useState(false);
  const [dataRow, setDataRow] = useState(false);
  const onClose = () => setVisible(false);

  const deliveries = useSelector(({ deliveries }) => deliveries.value);
  const { tableDeliveriesUser = false } = deliveries || {};
  const deliveriesAdmin = useSelector(({ deliveriesAdmin }) => deliveriesAdmin.value);
  const { tableDeliveriesAdmin = false } = deliveriesAdmin || {};
  const { isAdmin } = useSelector(({ userData }) => userData.value);
  const tableData = !isAdmin ? tableDeliveriesUser : tableDeliveriesAdmin;

  const PaginationTable = ({ filter: dataFilter, setFilter: setDataFilter }) => {
    const [width, setWidth] = useState(window.innerWidth);
    const breakpoint = 768;

    useEffect(() => {
      const handleResizeWindow = () => setWidth(window.innerWidth);
      window.addEventListener("resize", handleResizeWindow);
      return () => window.removeEventListener("resize", handleResizeWindow);
    }, []);

    const columns = [
      {
        title: "Date",
        dataIndex: "lastEvent",
        key: "lastEvent",
        width: 115,
        render: (lastEvent) => {
          const { date, time } = formatWIB(lastEvent);
          return (
            <div className="flex flex-col">
              <p className="mb-0">{date}</p>
              <p className="mb-0 text-secondary text-xs" type="secondary">
                {time}
              </p>
            </div>
          );
        },
      },
      {
        title: "Subject",
        dataIndex: "subject",
        key: "subject",
        ellipsis: true,
        ...(width > 991 && width < 1200 ? { width: 200 } : {}),
        render: (subject) => subject || "-",
      },
      {
        title: "From",
        dataIndex: "from",
        key: "from",
        ...(width > 1200 ? { width: width >= 1600 ? 220 : 200 } : {}),
        ellipsis: true,
      },
      {
        title: "Recipient",
        key: "recipient",
        dataIndex: "recipient",
        ...(width > 1200 ? { width: width >= 1600 ? 220 : 200 } : {}),
        ellipsis: true,
      },
      {
        title: "Status",
        key: "status",
        dataIndex: "status",
        width: 120,
        render: (status) => {
          const badge = statusBadge(status);
          return <Badge status={badge.status} text={badge.text} />;
        },
      },
      {
        title: "Attempts",
        key: "attemptCount",
        dataIndex: "attemptCount",
        width: 90,
        align: "center",
        render: (attemptCount) => attemptCount ?? "-",
      },
    ];

    return (
      <ConfigProvider renderEmpty={() => <EmptyWrapper {...emptyState} />}>
        <Table
          className="email-log-table"
          columns={columns}
          dataSource={tableData.rows}
          title={() => (
            <TableTitle
              title="Deliveries"
              desc={`Log pengiriman email (v2) ${process.env.REACT_APP_NAME}. Klik untuk melihat detail`}
              buttonRefresh={{
                onClick: () => {
                  (onRefresh || hitApi)();
                },
              }}
              loading={loading}
              pageLimit={{
                currentLimit: dataFilter.limit,
                onSelectLimit: (limit) => {
                  setDataFilter({ ...dataFilter, page: 1, limit });
                },
                disable: tableData?.count < 10,
              }}
              buttonExport={{
                dataCSV: tableData.rows.map((item) => ({
                  ...item,
                  dateWIB: formatWIBString(item.lastEvent),
                })),
                headerCSV: headerCSV,
                responsive: "md",
                fileName: isAdmin ? "Deliveries-Customer" : "Deliveries",
              }}
            />
          )}
          loading={{
            spinning: loading,
            indicator: <ICLoadingOutlined className="spinner-size m-auto" spin />,
          }}
          rowClassName="pointer"
          pagination={false}
          onRow={(record) => ({
            onClick: () => {
              setVisible(true);
              setDataRow(record);
            },
          })}
          rowKey={(record, index) => `${record.messageId}-${index}`}
          {...(width < breakpoint ? { scroll: { x: 1176 } } : {})}
        />
      </ConfigProvider>
    );
  };

  const badge = statusBadge(dataRow.status);

  return (
    <div className="px-6 py-2 bg-white">
      <PaginationTable filter={filter} setFilter={setFilter} />
      <div className="deliveries-pager flex items-center justify-end gap-x-4 py-4">
        <span className="text-secondary">Total: {formatNumber(tableData.count || 0)}</span>
        <span>Halaman {pageIndex}</span>
        <Button
          size="small"
          icon={<ICLeftOutlined />}
          disabled={!canPrev || loading}
          onClick={onPrev}>
          Sebelumnya
        </Button>
        <Button
          size="small"
          disabled={!tableData?.nextCursor || loading}
          onClick={() => onNext && onNext(tableData?.nextCursor)}>
          Berikutnya
          <ICRightOutlined />
        </Button>
      </div>
      <Drawer
        closable={false}
        className="drawer-log-detail title-log-email"
        title="Delivery Details"
        placement="right"
        onClose={onClose}
        size="large"
        open={visible}
        extra={
          <Space>
            <Button
              icon={<ICCloseOutlined />}
              type="text"
              onClick={onClose}
              style={{ color: "#0D9F3F" }}>
              Close
            </Button>
          </Space>
        }>
        <div className="main-drawer">
          <Descriptions
            layout="horizontal"
            column={1}
            bordered
            className="drawer-desc-wrapper"
            labelStyle={{ padding: "0 16px" }}
            contentStyle={{ padding: "13px 12px" }}>
            <Descriptions.Item label="Waktu">
              {formatWIBString(dataRow.lastEvent)}
            </Descriptions.Item>
            <Descriptions.Item label="Message ID">{dataRow.messageId || "-"}</Descriptions.Item>
            <Descriptions.Item label="Subject">{dataRow.subject || "-"}</Descriptions.Item>
            <Descriptions.Item label="Pengirim">{dataRow.from || "-"}</Descriptions.Item>
            <Descriptions.Item label="Penerima">{dataRow.recipient || "-"}</Descriptions.Item>
            <Descriptions.Item label="Status">
              <Badge status={badge.status} text={badge.text} />
            </Descriptions.Item>
            <Descriptions.Item label="DSN">{dataRow.dsn || "-"}</Descriptions.Item>
            <Descriptions.Item label="Sending Server">{dataRow.smtpOutHost || "-"}</Descriptions.Item>
            <Descriptions.Item label="Relay Host">
              {dataRow.relayHost ? `${dataRow.relayHost}${dataRow.relayPort ? `:${dataRow.relayPort}` : ""}` : "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Remote Response">
              {dataRow.remoteResponse || "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Quota Period">{dataRow.quotaPeriod || "-"}</Descriptions.Item>
          </Descriptions>

          <div className="delivery-attempts mt-6">
            <p className="font-semibold mb-3">Riwayat Percobaan ({dataRow.attemptCount ?? 0})</p>
            {Array.isArray(dataRow.attempts) && dataRow.attempts.length > 0 ? (
              <Timeline>
                {dataRow.attempts.map((attempt, index) => (
                  <Timeline.Item color={dsnColor(attempt.dsn)} key={index}>
                    <p className="mb-1">
                      <strong>{capitalizeFirstLetter(attempt.status || "-")}</strong>
                      {attempt.dsn ? ` · ${attempt.dsn}` : ""}
                    </p>
                    <p className="mb-1 text-secondary text-xs">{formatWIBString(attempt.ts)}</p>
                    {attempt.response && <p className="mb-0 text-xs">{attempt.response}</p>}
                  </Timeline.Item>
                ))}
              </Timeline>
            ) : (
              <p className="text-secondary">Tidak ada riwayat percobaan.</p>
            )}
          </div>
        </div>
      </Drawer>
    </div>
  );
};

export default TableDeliveries;
